"use strict";
/**
 * RBAC catalog (TZ §5).
 *
 * Permissions are granular, dot-separated `resource.action` strings. Roles are
 * just named bundles of them: the eleven system roles below are seeded into
 * every tenant, and admins may build custom roles on top of the same catalog
 * (e.g. "Senior Cashier" = orders.view + orders.create + orders.cancel +
 * payments.receive + cashbox.view).
 *
 * The backend authorises against PERMISSIONS ONLY — never against a role name.
 * That way a custom role behaves exactly like a system role, and adding a
 * permission to a role never requires touching a guard.
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.DEFAULT_ROLE_PERMISSIONS = exports.PLATFORM_ROLES = exports.SystemRole = exports.PERMISSION_GROUPS = exports.ALL_PERMISSIONS = exports.Permission = void 0;
exports.isPlatformRole = isPlatformRole;
exports.hasAllPermissions = hasAllPermissions;
exports.hasAnyPermission = hasAnyPermission;
/* -------------------------------------------------------------------------- */
/* Permission catalog                                                         */
/* -------------------------------------------------------------------------- */
exports.Permission = {
    // --- Platform level: super admin only (TZ §6) ---
    TENANTS_VIEW: 'tenants.view',
    TENANTS_CREATE: 'tenants.create',
    TENANTS_UPDATE: 'tenants.update',
    TENANTS_BLOCK: 'tenants.block',
    TENANTS_DELETE: 'tenants.delete',
    SUBSCRIPTIONS_VIEW: 'subscriptions.view',
    SUBSCRIPTIONS_MANAGE: 'subscriptions.manage',
    PLATFORM_ANALYTICS_VIEW: 'platform.analytics.view',
    /**
     * Attaching a payment provider's credentials to a restaurant.
     *
     * Platform-level rather than tenant-level: the merchant account is opened by
     * the platform on the restaurant's behalf, and a leaked key lets anyone
     * charge against it. Separate from `settings.update` so the broad "edit
     * company settings" right does not quietly carry it.
     */
    PAYMENT_PROVIDERS_MANAGE: 'payment_providers.manage',
    // --- Company / settings ---
    SETTINGS_VIEW: 'settings.view',
    SETTINGS_UPDATE: 'settings.update',
    BRANDING_UPDATE: 'branding.update',
    // --- Branches (TZ §8) ---
    BRANCHES_VIEW: 'branches.view',
    BRANCHES_CREATE: 'branches.create',
    BRANCHES_UPDATE: 'branches.update',
    BRANCHES_DELETE: 'branches.delete',
    // --- Staff & access control ---
    EMPLOYEES_VIEW: 'employees.view',
    EMPLOYEES_CREATE: 'employees.create',
    EMPLOYEES_UPDATE: 'employees.update',
    EMPLOYEES_DELETE: 'employees.delete',
    ROLES_VIEW: 'roles.view',
    ROLES_CREATE: 'roles.create',
    ROLES_UPDATE: 'roles.update',
    ROLES_DELETE: 'roles.delete',
    PERMISSIONS_VIEW: 'permissions.view',
    PERMISSIONS_ASSIGN: 'permissions.assign',
    // --- Menu (TZ §9) ---
    CATEGORIES_VIEW: 'categories.view',
    CATEGORIES_CREATE: 'categories.create',
    CATEGORIES_UPDATE: 'categories.update',
    CATEGORIES_DELETE: 'categories.delete',
    PRODUCTS_VIEW: 'products.view',
    PRODUCTS_CREATE: 'products.create',
    PRODUCTS_UPDATE: 'products.update',
    PRODUCTS_DELETE: 'products.delete',
    /** Separate from products.update: price changes are audited and sensitive. */
    PRODUCTS_PRICE_UPDATE: 'products.price.update',
    /** Toggling stop-list / availability per branch — safe for shift managers. */
    PRODUCTS_AVAILABILITY_UPDATE: 'products.availability.update',
    MODIFIERS_VIEW: 'modifiers.view',
    MODIFIERS_MANAGE: 'modifiers.manage',
    // --- Orders (TZ §12) ---
    ORDERS_VIEW: 'orders.view',
    /** See orders of every branch, not just the ones the user is assigned to. */
    ORDERS_VIEW_ALL_BRANCHES: 'orders.view.all_branches',
    ORDERS_CREATE: 'orders.create',
    ORDERS_UPDATE: 'orders.update',
    ORDERS_CANCEL: 'orders.cancel',
    ORDERS_REFUND: 'orders.refund',
    ORDERS_CHANGE_STATUS: 'orders.change_status',
    ORDERS_ASSIGN_COURIER: 'orders.assign_courier',
    // --- Kitchen (TZ §21) ---
    KITCHEN_VIEW: 'kitchen.view',
    KITCHEN_UPDATE_STATUS: 'kitchen.update_status',
    // --- POS & cash (TZ §17, §19) ---
    POS_ACCESS: 'pos.access',
    PAYMENTS_VIEW: 'payments.view',
    PAYMENTS_RECEIVE: 'payments.receive',
    PAYMENTS_REFUND: 'payments.refund',
    CASHBOX_VIEW: 'cashbox.view',
    CASHBOX_OPEN_SHIFT: 'cashbox.open_shift',
    CASHBOX_CLOSE_SHIFT: 'cashbox.close_shift',
    CASHBOX_TRANSACTION: 'cashbox.transaction',
    /** Seeing another cashier's shift totals, not only one's own. */
    CASHBOX_VIEW_ALL_SHIFTS: 'cashbox.view_all_shifts',
    // --- Couriers (TZ §23-§28) ---
    COURIERS_VIEW: 'couriers.view',
    COURIERS_CREATE: 'couriers.create',
    COURIERS_UPDATE: 'couriers.update',
    COURIERS_DELETE: 'couriers.delete',
    COURIERS_TRACK: 'couriers.track',
    COURIER_WALLET_VIEW: 'couriers.wallet.view',
    COURIER_WALLET_SETTLE: 'couriers.wallet.settle',
    /** What the courier app itself needs: read own jobs, move own delivery. */
    DELIVERIES_VIEW_OWN: 'deliveries.view_own',
    DELIVERIES_UPDATE_OWN: 'deliveries.update_own',
    // --- Customers / CRM (TZ §31, §32) ---
    CUSTOMERS_VIEW: 'customers.view',
    CUSTOMERS_UPDATE: 'customers.update',
    CUSTOMERS_DELETE: 'customers.delete',
    CUSTOMERS_EXPORT: 'customers.export',
    // --- Marketing (TZ §10, §33, §34) ---
    PROMOTIONS_VIEW: 'promotions.view',
    PROMOTIONS_MANAGE: 'promotions.manage',
    PROMO_CODES_VIEW: 'promo_codes.view',
    PROMO_CODES_MANAGE: 'promo_codes.manage',
    LOYALTY_VIEW: 'loyalty.view',
    LOYALTY_MANAGE: 'loyalty.manage',
    /**
     * Sending a marketing message to the restaurant's opted-in customers through
     * its bot. Its own permission, not part of `promotions.manage`: a message
     * lands in people's Telegram, so it is a different kind of mistake from a
     * misconfigured discount.
     */
    MARKETING_BROADCAST: 'marketing.broadcast',
    // --- Tables (TZ §29, §30) ---
    TABLES_VIEW: 'tables.view',
    TABLES_MANAGE: 'tables.manage',
    WAITER_CALLS_HANDLE: 'waiter_calls.handle',
    /** Taking and updating bookings — separate from `tables.manage`, which
        can re-issue QR codes and should not come with answering the phone. */
    RESERVATIONS_MANAGE: 'reservations.manage',
    // --- Inventory (TZ §29-§31) ---
    INVENTORY_VIEW: 'inventory.view',
    /**
     * Covers stock levels, suppliers AND recipes.
     *
     * Not split finer on purpose: a storekeeper who records a delivery is the
     * same person who corrects a level after a stock-take, and a recipe is what
     * makes the deduction happen at all. Three permissions here would be three
     * things an owner has to remember to grant together.
     */
    INVENTORY_MANAGE: 'inventory.manage',
    // --- Telegram (TZ §15) ---
    TELEGRAM_VIEW: 'telegram.view',
    /**
     * Connecting the bot token itself. Grouped under Platform (below), because
     * the platform registers each restaurant's bot for it; a restaurant still
     * reads its own configuration and routing through `telegram.view`.
     */
    TELEGRAM_MANAGE: 'telegram.manage',
    /**
     * Choosing which of the restaurant's own groups each event goes to.
     *
     * Tenant-level, unlike the token above: deciding that Chilonzor's orders
     * land in Chilonzor's group is the restaurant's operational call, and
     * nothing here can redirect the bot itself.
     */
    TELEGRAM_ROUTES_MANAGE: 'telegram.routes.manage',
    // --- Finance & reporting (TZ §27) ---
    FINANCE_VIEW: 'finance.view',
    FINANCE_MANAGE: 'finance.manage',
    REPORTS_VIEW: 'reports.view',
    REPORTS_EXPORT: 'reports.export',
    ANALYTICS_VIEW: 'analytics.view',
    // --- Audit (TZ §37) ---
    AUDIT_VIEW: 'audit.view',
};
exports.ALL_PERMISSIONS = Object.values(exports.Permission);
/**
 * Display grouping for the admin "Roles & permissions" screen. The key is the
 * group label; membership is derived from the permission's resource prefix.
 */
exports.PERMISSION_GROUPS = {
    Platform: [
        exports.Permission.TENANTS_VIEW,
        exports.Permission.TENANTS_CREATE,
        exports.Permission.TENANTS_UPDATE,
        exports.Permission.TENANTS_BLOCK,
        exports.Permission.TENANTS_DELETE,
        exports.Permission.SUBSCRIPTIONS_VIEW,
        exports.Permission.SUBSCRIPTIONS_MANAGE,
        exports.Permission.PLATFORM_ANALYTICS_VIEW,
        // Integrations the platform wires up on a restaurant's behalf. Listing
        // them here is what keeps them out of OWNER and COMPANY_ADMIN, both of
        // which are defined as "everything except the Platform group".
        exports.Permission.TELEGRAM_MANAGE,
        exports.Permission.PAYMENT_PROVIDERS_MANAGE,
    ],
    Company: [
        exports.Permission.SETTINGS_VIEW,
        exports.Permission.SETTINGS_UPDATE,
        exports.Permission.BRANDING_UPDATE,
    ],
    Branches: [
        exports.Permission.BRANCHES_VIEW,
        exports.Permission.BRANCHES_CREATE,
        exports.Permission.BRANCHES_UPDATE,
        exports.Permission.BRANCHES_DELETE,
    ],
    Staff: [
        exports.Permission.EMPLOYEES_VIEW,
        exports.Permission.EMPLOYEES_CREATE,
        exports.Permission.EMPLOYEES_UPDATE,
        exports.Permission.EMPLOYEES_DELETE,
        exports.Permission.ROLES_VIEW,
        exports.Permission.ROLES_CREATE,
        exports.Permission.ROLES_UPDATE,
        exports.Permission.ROLES_DELETE,
        exports.Permission.PERMISSIONS_VIEW,
        exports.Permission.PERMISSIONS_ASSIGN,
    ],
    Menu: [
        exports.Permission.CATEGORIES_VIEW,
        exports.Permission.CATEGORIES_CREATE,
        exports.Permission.CATEGORIES_UPDATE,
        exports.Permission.CATEGORIES_DELETE,
        exports.Permission.PRODUCTS_VIEW,
        exports.Permission.PRODUCTS_CREATE,
        exports.Permission.PRODUCTS_UPDATE,
        exports.Permission.PRODUCTS_DELETE,
        exports.Permission.PRODUCTS_PRICE_UPDATE,
        exports.Permission.PRODUCTS_AVAILABILITY_UPDATE,
        exports.Permission.MODIFIERS_VIEW,
        exports.Permission.MODIFIERS_MANAGE,
    ],
    Orders: [
        exports.Permission.ORDERS_VIEW,
        exports.Permission.ORDERS_VIEW_ALL_BRANCHES,
        exports.Permission.ORDERS_CREATE,
        exports.Permission.ORDERS_UPDATE,
        exports.Permission.ORDERS_CANCEL,
        exports.Permission.ORDERS_REFUND,
        exports.Permission.ORDERS_CHANGE_STATUS,
        exports.Permission.ORDERS_ASSIGN_COURIER,
    ],
    Kitchen: [exports.Permission.KITCHEN_VIEW, exports.Permission.KITCHEN_UPDATE_STATUS],
    'POS & Cash': [
        exports.Permission.POS_ACCESS,
        exports.Permission.PAYMENTS_VIEW,
        exports.Permission.PAYMENTS_RECEIVE,
        exports.Permission.PAYMENTS_REFUND,
        exports.Permission.CASHBOX_VIEW,
        exports.Permission.CASHBOX_OPEN_SHIFT,
        exports.Permission.CASHBOX_CLOSE_SHIFT,
        exports.Permission.CASHBOX_TRANSACTION,
        exports.Permission.CASHBOX_VIEW_ALL_SHIFTS,
    ],
    Delivery: [
        exports.Permission.COURIERS_VIEW,
        exports.Permission.COURIERS_CREATE,
        exports.Permission.COURIERS_UPDATE,
        exports.Permission.COURIERS_DELETE,
        exports.Permission.COURIERS_TRACK,
        exports.Permission.COURIER_WALLET_VIEW,
        exports.Permission.COURIER_WALLET_SETTLE,
        exports.Permission.DELIVERIES_VIEW_OWN,
        exports.Permission.DELIVERIES_UPDATE_OWN,
    ],
    Customers: [
        exports.Permission.CUSTOMERS_VIEW,
        exports.Permission.CUSTOMERS_UPDATE,
        exports.Permission.CUSTOMERS_DELETE,
        exports.Permission.CUSTOMERS_EXPORT,
    ],
    Marketing: [
        exports.Permission.PROMOTIONS_VIEW,
        exports.Permission.PROMOTIONS_MANAGE,
        exports.Permission.PROMO_CODES_VIEW,
        exports.Permission.PROMO_CODES_MANAGE,
        exports.Permission.LOYALTY_VIEW,
        exports.Permission.LOYALTY_MANAGE,
        exports.Permission.MARKETING_BROADCAST,
    ],
    Tables: [
        exports.Permission.TABLES_VIEW,
        exports.Permission.TABLES_MANAGE,
        exports.Permission.WAITER_CALLS_HANDLE,
        exports.Permission.RESERVATIONS_MANAGE,
    ],
    Inventory: [exports.Permission.INVENTORY_VIEW, exports.Permission.INVENTORY_MANAGE],
    Telegram: [exports.Permission.TELEGRAM_VIEW, exports.Permission.TELEGRAM_ROUTES_MANAGE],
    Finance: [
        exports.Permission.FINANCE_VIEW,
        exports.Permission.FINANCE_MANAGE,
        exports.Permission.REPORTS_VIEW,
        exports.Permission.REPORTS_EXPORT,
        exports.Permission.ANALYTICS_VIEW,
    ],
    Audit: [exports.Permission.AUDIT_VIEW],
};
/* -------------------------------------------------------------------------- */
/* System roles                                                               */
/* -------------------------------------------------------------------------- */
exports.SystemRole = {
    SUPER_ADMIN: 'SUPER_ADMIN',
    OWNER: 'OWNER',
    COMPANY_ADMIN: 'COMPANY_ADMIN',
    BRANCH_MANAGER: 'BRANCH_MANAGER',
    CASHIER: 'CASHIER',
    KITCHEN: 'KITCHEN',
    COURIER: 'COURIER',
    FINANCE: 'FINANCE',
    OPERATOR: 'OPERATOR',
    MARKETING: 'MARKETING',
    SUPPORT: 'SUPPORT',
};
/**
 * The only role that lives outside any tenant. A SUPER_ADMIN user has
 * `tenantId = null` and its token carries no tenant, which is what lets the
 * tenant-scoping Prisma extension run unscoped for it.
 */
exports.PLATFORM_ROLES = [exports.SystemRole.SUPER_ADMIN];
function isPlatformRole(role) {
    return exports.PLATFORM_ROLES.includes(role);
}
/* -------------------------------------------------------------------------- */
/* Default role → permission mapping                                          */
/* -------------------------------------------------------------------------- */
const READ_ONLY_OPERATIONS = [
    exports.Permission.ORDERS_VIEW,
    exports.Permission.ORDERS_VIEW_ALL_BRANCHES,
    exports.Permission.PRODUCTS_VIEW,
    exports.Permission.CATEGORIES_VIEW,
    exports.Permission.MODIFIERS_VIEW,
    exports.Permission.BRANCHES_VIEW,
    exports.Permission.CUSTOMERS_VIEW,
];
/**
 * Permissions each system role is seeded with. SUPER_ADMIN is intentionally
 * absent: it is granted `ALL_PERMISSIONS` at seed time and additionally
 * short-circuits the permissions guard, so new permissions never need to be
 * back-filled into it.
 */
exports.DEFAULT_ROLE_PERMISSIONS = {
    /** Business owner — everything inside their own tenant. */
    OWNER: exports.ALL_PERMISSIONS.filter((p) => !exports.PERMISSION_GROUPS.Platform.includes(p)),
    /** Day-to-day company administrator: no branding/billing surgery. */
    COMPANY_ADMIN: exports.ALL_PERMISSIONS.filter((p) => !exports.PERMISSION_GROUPS.Platform.includes(p) &&
        p !== exports.Permission.BRANDING_UPDATE &&
        p !== exports.Permission.TENANTS_DELETE &&
        p !== exports.Permission.BRANCHES_DELETE),
    /** Runs one branch: full floor control, no company-wide configuration. */
    BRANCH_MANAGER: [
        exports.Permission.SETTINGS_VIEW,
        exports.Permission.BRANCHES_VIEW,
        exports.Permission.EMPLOYEES_VIEW,
        exports.Permission.CATEGORIES_VIEW,
        exports.Permission.PRODUCTS_VIEW,
        exports.Permission.PRODUCTS_AVAILABILITY_UPDATE,
        exports.Permission.MODIFIERS_VIEW,
        exports.Permission.ORDERS_VIEW,
        exports.Permission.ORDERS_CREATE,
        exports.Permission.ORDERS_UPDATE,
        exports.Permission.ORDERS_CANCEL,
        exports.Permission.ORDERS_CHANGE_STATUS,
        exports.Permission.ORDERS_ASSIGN_COURIER,
        exports.Permission.KITCHEN_VIEW,
        exports.Permission.KITCHEN_UPDATE_STATUS,
        exports.Permission.POS_ACCESS,
        exports.Permission.PAYMENTS_VIEW,
        exports.Permission.PAYMENTS_RECEIVE,
        exports.Permission.PAYMENTS_REFUND,
        exports.Permission.CASHBOX_VIEW,
        exports.Permission.CASHBOX_VIEW_ALL_SHIFTS,
        exports.Permission.CASHBOX_OPEN_SHIFT,
        exports.Permission.CASHBOX_CLOSE_SHIFT,
        exports.Permission.CASHBOX_TRANSACTION,
        exports.Permission.COURIERS_VIEW,
        exports.Permission.COURIERS_TRACK,
        exports.Permission.COURIER_WALLET_VIEW,
        exports.Permission.COURIER_WALLET_SETTLE,
        // A branch manager runs the shelf: deliveries land on their shift and a
        // stock-take is their job, so withholding this would make the module
        // unusable at the only place stock physically exists.
        exports.Permission.INVENTORY_VIEW,
        exports.Permission.INVENTORY_MANAGE,
        exports.Permission.CUSTOMERS_VIEW,
        exports.Permission.TABLES_VIEW,
        exports.Permission.TABLES_MANAGE,
        exports.Permission.WAITER_CALLS_HANDLE,
        exports.Permission.RESERVATIONS_MANAGE,
        exports.Permission.PROMOTIONS_VIEW,
        exports.Permission.PROMO_CODES_VIEW,
        exports.Permission.REPORTS_VIEW,
        exports.Permission.ANALYTICS_VIEW,
    ],
    /** Till operator (TZ §5 "Senior Cashier" is a custom role on top of this). */
    CASHIER: [
        exports.Permission.POS_ACCESS,
        // The POS header shows which branch the till belongs to, and the order
        // form needs the branch's delivery fee and minimum — without this the
        // terminal cannot render its own identity.
        exports.Permission.BRANCHES_VIEW,
        exports.Permission.ORDERS_VIEW,
        exports.Permission.ORDERS_CREATE,
        exports.Permission.ORDERS_UPDATE,
        exports.Permission.ORDERS_CHANGE_STATUS,
        exports.Permission.PRODUCTS_VIEW,
        exports.Permission.CATEGORIES_VIEW,
        exports.Permission.MODIFIERS_VIEW,
        exports.Permission.PAYMENTS_VIEW,
        exports.Permission.PAYMENTS_RECEIVE,
        exports.Permission.CASHBOX_VIEW,
        exports.Permission.CASHBOX_OPEN_SHIFT,
        exports.Permission.CASHBOX_CLOSE_SHIFT,
        exports.Permission.CASHBOX_TRANSACTION,
        exports.Permission.CUSTOMERS_VIEW,
        exports.Permission.TABLES_VIEW,
        // The till is where a walk-in asks for a table tonight.
        exports.Permission.RESERVATIONS_MANAGE,
    ],
    /** Kitchen display only — cannot see money. */
    KITCHEN: [
        exports.Permission.KITCHEN_VIEW,
        exports.Permission.KITCHEN_UPDATE_STATUS,
        exports.Permission.ORDERS_VIEW,
        exports.Permission.PRODUCTS_VIEW,
        // The KDS picks its branch on first run.
        exports.Permission.BRANCHES_VIEW,
        // Marking something as run out is a kitchen action, not an admin one.
        exports.Permission.PRODUCTS_AVAILABILITY_UPDATE,
    ],
    /** The courier mobile app: own jobs, own wallet. Nothing else. */
    COURIER: [
        exports.Permission.DELIVERIES_VIEW_OWN,
        exports.Permission.DELIVERIES_UPDATE_OWN,
        exports.Permission.COURIER_WALLET_VIEW,
        // Needed to show the pickup branch's address and phone on the job card.
        exports.Permission.BRANCHES_VIEW,
    ],
    FINANCE: [
        exports.Permission.FINANCE_VIEW,
        exports.Permission.FINANCE_MANAGE,
        // Read-only: the value sitting on the shelves is a balance-sheet number,
        // but moving stock is the storekeeper's job, not finance's.
        exports.Permission.INVENTORY_VIEW,
        exports.Permission.PAYMENTS_VIEW,
        exports.Permission.PAYMENTS_REFUND,
        exports.Permission.CASHBOX_VIEW,
        exports.Permission.CASHBOX_VIEW_ALL_SHIFTS,
        exports.Permission.COURIER_WALLET_VIEW,
        exports.Permission.COURIER_WALLET_SETTLE,
        exports.Permission.ORDERS_VIEW,
        exports.Permission.ORDERS_VIEW_ALL_BRANCHES,
        exports.Permission.ORDERS_REFUND,
        exports.Permission.REPORTS_VIEW,
        exports.Permission.REPORTS_EXPORT,
        exports.Permission.ANALYTICS_VIEW,
        exports.Permission.AUDIT_VIEW,
    ],
    /** Call-centre / dispatcher. */
    OPERATOR: [
        exports.Permission.ORDERS_VIEW,
        exports.Permission.ORDERS_VIEW_ALL_BRANCHES,
        exports.Permission.ORDERS_CREATE,
        exports.Permission.ORDERS_UPDATE,
        exports.Permission.ORDERS_CANCEL,
        exports.Permission.ORDERS_CHANGE_STATUS,
        exports.Permission.ORDERS_ASSIGN_COURIER,
        exports.Permission.PRODUCTS_VIEW,
        exports.Permission.CATEGORIES_VIEW,
        exports.Permission.MODIFIERS_VIEW,
        exports.Permission.BRANCHES_VIEW,
        exports.Permission.CUSTOMERS_VIEW,
        exports.Permission.CUSTOMERS_UPDATE,
        exports.Permission.COURIERS_VIEW,
        exports.Permission.COURIERS_TRACK,
        exports.Permission.PROMO_CODES_VIEW,
        // Bookings mostly arrive as phone calls, which is the call centre.
        exports.Permission.RESERVATIONS_MANAGE,
    ],
    MARKETING: [
        exports.Permission.PROMOTIONS_VIEW,
        exports.Permission.PROMOTIONS_MANAGE,
        exports.Permission.PROMO_CODES_VIEW,
        exports.Permission.PROMO_CODES_MANAGE,
        exports.Permission.LOYALTY_VIEW,
        exports.Permission.LOYALTY_MANAGE,
        exports.Permission.MARKETING_BROADCAST,
        exports.Permission.CUSTOMERS_VIEW,
        exports.Permission.CUSTOMERS_EXPORT,
        exports.Permission.PRODUCTS_VIEW,
        exports.Permission.CATEGORIES_VIEW,
        exports.Permission.REPORTS_VIEW,
        exports.Permission.ANALYTICS_VIEW,
    ],
    SUPPORT: [...READ_ONLY_OPERATIONS, exports.Permission.CUSTOMERS_UPDATE, exports.Permission.AUDIT_VIEW],
};
/* -------------------------------------------------------------------------- */
/* Runtime helpers                                                            */
/* -------------------------------------------------------------------------- */
/** True when `granted` covers every entry of `required`. */
function hasAllPermissions(granted, required) {
    return required.every((p) => granted.includes(p));
}
/** True when `granted` covers at least one entry of `required`. */
function hasAnyPermission(granted, required) {
    return required.some((p) => granted.includes(p));
}
//# sourceMappingURL=rbac.js.map